import React, { useEffect, useRef, useState } from "react";
import Avatar from "../../../../components/ui/avatar/Avatar";
import ButtonV2 from "../../../../components/ui/button/v2";
import CansoEditor from "../../../../components/ui/editor";
import { cn } from "../../../../lib/utils";

export interface CommentComposerProps {
  currentUserName: string;
  value: string;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const CommentComposer: React.FC<CommentComposerProps> = ({
  currentUserName,
  value,
  onChange,
  onCancel,
  onSubmit,
  isSubmitting,
}) => {
  const hasText = value.trim().length > 0;
  const isDisabled = !hasText || isSubmitting;
  const [editorResetKey, setEditorResetKey] = useState(0);
  const prevValueRef = useRef<string>(value);

  useEffect(() => {
    const prev = prevValueRef.current;
    if (prev.length > 0 && value.length === 0) {
      setEditorResetKey((k) => k + 1);
    }
    prevValueRef.current = value;
  }, [value]);

  const renderTextArea = () => {
    return (
      <div
        className={cn(
          "w-full rounded-md border border-[#E5E5E5] bg-white",
          "overflow-hidden",
          isSubmitting && "bg-gray-50 cursor-not-allowed",
        )}
      >
        <CansoEditor
          key={editorResetKey}
          id={`comment-composer-editor-${editorResetKey}`}
          markdown={value}
          onChange={onChange}
          placeholder="Write a comment..."
          readOnly={isSubmitting}
          autoHideToolbar={true}
          allowFullScreen={true}
          className="rounded-md"
          contentEditableClassName={cn(
            "h-[100px] overflow-y-auto",
            "px-3 py-2",
            "font-sans font-normal text-sm leading-5 text-[#0A0A0A]",
          )}
        />
      </div>
    );
  };

  const renderFooterActions = () => {
    return (
      <div className="flex justify-end gap-2">
        <ButtonV2
          variant="ghost"
          size="md"
          disabled={isDisabled}
          className={cn(
            "w-[79px] h-[36px] px-4 py-2 rounded-md",
            "disabled:opacity-50",
          )}
          textClassName="font-sans font-medium text-sm leading-5 text-[#0A0A0A]"
          onClick={onCancel}
        >
          Cancel
        </ButtonV2>

        <ButtonV2
          variant="primary"
          size="md"
          disabled={isDisabled}
          loading={isSubmitting}
          className={cn(
            "w-[98px] h-[36px] px-4 py-2 rounded-md",
            "disabled:opacity-50",
          )}
          textClassName="font-sans font-medium text-sm leading-5"
          onClick={onSubmit}
        >
          Comment
        </ButtonV2>
      </div>
    );
  };

  return (
    <div className="flex gap-4">
      <Avatar name={currentUserName} />
      <div className="w-full flex flex-col gap-4">
        {renderTextArea()}
        {renderFooterActions()}
      </div>
    </div>
  );
};

export default CommentComposer;
